'use strict';

import { action, observable, observe, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";
import {isCoursePlaceholder} from "./CourseUtils";
import termStore from "@uw-it-sis/lib-react/lib/TermStore";
import planItemStore from "./PlanItemStore";
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import events from '@uw-it-sis/lib-react/lib/Events';
import PlanEvents from './PlanEvents';
import feedbackMessagesStore from '@uw-it-sis/lib-react/lib/FeedbackMessagesStore';
import Level from '@uw-it-sis/lib-react/lib/FeedbackMessagesLevel';

class MoveCourseOnPlanFormStore {

    state = States.initial;

    data = {};

    constructor() {
        makeObservable(this, {
            state: observable,
            data: observable,
            mount: action,
            checkDataLoadStates: action,
            finishPlanItemMove: action,
            errorPlanItemMove: action,
            moveCourse: action,
            updateFormData: action
        });

        this.dataLoadStates = {
            internalProcessing: States.initial,
            futureTerms: States.initial,
        };
    }

    /**
     * Initialize form data.
     * There is no widget for courseCode so it has to be initialized.
     */
    mount(courseCode, planItemId, termId, note, credit, placeHolderText) {

        this.dataLoadStates.internalProcessing = States.pending;

        this.checkDataLoadStates();

        // if we are re-initializing, and aren't in the middle of a move ( which we should not be )
        // clear any existing failed message
        if (!this.data.moving) {
            this.data.moving = false;
            this.data.submitted = false;
            this.data.moveFailedMessage = undefined;
        }

        this.data.termId = termId;
        if ( note === undefined || note == null ) {
            note = "";
        }
        if ( credit === undefined || credit === null ) {
            credit = "";
        }
        this.data.formData = {
            planItemId: planItemId,
            code: courseCode,
            termId: "",
            note: note,
            credit: credit,
            backup: false,
            placeHolderText: placeHolderText
        };

        events.listen(PlanEvents.planItemUpdated, (eventObject) => {
            if (eventObject.planItemId === this.data.formData.planItemId) {
                console.log("Plan item update finished, event emitted was: " + JSON.stringify(eventObject));
                this.finishPlanItemMove();
            }
        });

        events.listen(PlanEvents.planItemError, (eventObject) => {
            if (eventObject.planItemId === this.data.formData.planItemId) {
                console.log("Plan item update errored, event emitted was: " + JSON.stringify(eventObject));
                this.errorPlanItemMove(eventObject.errMessage);
            }
        });

        this.termStoreDisposer = observe(termStore, "state", function(change) {
            this.dataLoadStates.futureTerms = change.newValue;

            if (this.dataLoadStates.futureTerms === States.done) {
                let firstOptionTermId = termStore.get('futureTerms').filter(term => term.id !== this.data.termId)[0].id;
                this.data.formData.termId = firstOptionTermId;
            }

            this.checkDataLoadStates();
        }.bind(this));

        termStore.load('futureTerms');

        this.dataLoadStates.internalProcessing = States.done;

        this.checkDataLoadStates();
    }

    unmount() {
        this.termStoreDisposer();

        events.remove(PlanEvents.planItemUpdated, (eventObject) => {
            if (eventObject.planItemId === this.data.formData.planItemId) {
                this.finishPlanItemMove();
            }
        });

        events.remove(PlanEvents.planItemError, (eventObject) => {
            if (eventObject.planItemId === this.data.formData.planItemId) {
                this.errorPlanItemMove(eventObject.errMessage);
            }
        });
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    finishPlanItemMove() {
        this.data.moving = false;
    }

    errorPlanItemMove(errorMessage) {
        this.data.moveFailedMessage = errorMessage;
        this.data.moving = false;
    }

    moveCourse() {
        if ( ! this._isRequestValid() ) {
            return;
        }
        this.data.moveFailedMessage = undefined;
        this.data.moving = true;
        this.data.submitted = true;

        let formData = this.getFormData();

        if (isCoursePlaceholder(formData.code) || formData.code.includes("|")) {
            planItemStore.updatePlaceHolder(formData.planItemId, formData.code, formData.termId, formData.note, formData.backup, formData.credit, formData.placeHolderText);
        } else {
            planItemStore.update(formData.planItemId, formData.code, formData.termId, formData.note, formData.credit);
        }
    }

    /**
     * Allows inputs on the form to communicate their values as they are changed.
     */
    updateFormData(property, newValue) {
        if ( ! isEmpty(this.data) ) {
            this.data.formData[property] = newValue;
        }
    }

    getFormData() {
        return this.data.formData;
    }

    getMoveFailedMessage() {
        return this.data.moveFailedMessage;
    }

    isMoving() {
        return this.data.moving;
    }

    isSubmitted() {
        return this.data.submitted;
    }

    /**
     * Pass-thru to termStore data for future terms
     */
    getFutureTerms() {
        return termStore.get('futureTerms');
    }



    /**
     * Validate the form that was passed in from the form.
     */
    _isRequestValid() {

        let formData = this.getFormData();

        let rVal = true;

        if ( isEmpty(formData) ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Form data object was missing."});
            //  Bail out if we don't have any of the form data.
            return false;
        }

        if ( isEmpty(formData.code) ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Course Code property was missing."});
            rVal = false;
        }

        if ( isEmpty(formData.termId) ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Term Id property was missing."});
            rVal = false;
        }

        //  It's okay for this one to be empty, but not undefined.
        if ( formData.note === undefined ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Note property was missing."});
            rVal = false;
        }

        if ( formData.backup === undefined ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "The 'is backup' flag was missing."});
            rVal = false;
        }
        return rVal;
    }

}

export default new MoveCourseOnPlanFormStore();